<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import * as XLSX from 'xlsx';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const fileInput = ref(null);
const isProcessing = ref(false);
const uploadError = ref('');
const uploadStats = ref(null);

const currentPhoneNumbers = computed(() => {
  if (!props.modelValue) return [];
  return props.modelValue
    .split(/[\n,]/)
    .map(num => num.trim())
    .filter(num => num.length > 0);
});

const phoneNumberCount = computed(() => currentPhoneNumbers.value.length);

const validatePhoneNumber = phoneNumber => {
  try {
    // Limpiar el número
    const cleaned = phoneNumber.trim();

    // Si no empieza con +, no es válido en E.164
    if (!cleaned.startsWith('+')) {
      return { valid: false, number: cleaned };
    }

    // Validar con libphonenumber-js
    if (isValidPhoneNumber(cleaned)) {
      const parsed = parsePhoneNumber(cleaned);
      return {
        valid: true,
        number: parsed.number, // Formato E.164
        country: parsed.country,
      };
    }

    return { valid: false, number: cleaned };
  } catch (error) {
    return { valid: false, number: phoneNumber };
  }
};

const parseFile = async file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Leer la primera hoja
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

        // Convertir a JSON
        const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        // Extraer números (primera columna)
        const numbers = rows
          .map((row, index) => ({
            value: Array.isArray(row) ? row[0] : row,
            index,
          }))
          .filter(({ value, index }) => {
            // Filtrar celdas vacías
            if (value === undefined || value === null || value === '')
              return false;

            const strValue = String(value).trim().toLowerCase();

            // Ignorar primera fila si parece un encabezado
            if (
              index === 0 &&
              (strValue.includes('phone') ||
                strValue.includes('number') ||
                strValue.includes('telefono') ||
                strValue.includes('teléfono') ||
                strValue.includes('numero') ||
                strValue.includes('número'))
            ) {
              return false;
            }

            return true;
          })
          .map(({ value }) => {
            let strValue = String(value).trim();

            // Si el número no empieza con '+' pero parece un número válido (solo dígitos)
            // agregamos el '+' automáticamente
            if (!strValue.startsWith('+') && /^\d+$/.test(strValue)) {
              strValue = '+' + strValue;
            }

            return strValue;
          });

        resolve(numbers);
      } catch (error) {
        reject(new Error(t('CAMPAIGN.BUHOTEXT.PHONE_UPLOADER.PARSE_ERROR')));
      }
    };

    reader.onerror = () => {
      reject(new Error(t('CAMPAIGN.BUHOTEXT.PHONE_UPLOADER.READ_ERROR')));
    };

    reader.readAsArrayBuffer(file);
  });
};

const processNumbers = async numbers => {
  const validated = numbers.map(validatePhoneNumber);

  const valid = validated.filter(v => v.valid);
  const invalid = validated.filter(v => !v.valid);

  uploadStats.value = {
    total: numbers.length,
    valid: valid.length,
    invalid: invalid.length,
    invalidNumbers: invalid.map(v => v.number),
  };

  // Solo agregar números válidos
  if (valid.length > 0) {
    const validNumbers = valid.map(v => v.number).join('\n');

    // Combinar con números existentes si hay
    const existing = props.modelValue.trim();
    const combined = existing ? `${existing}\n${validNumbers}` : validNumbers;

    emit('update:modelValue', combined);
  }
};

const handleFileUpload = async event => {
  const file = event.target.files[0];
  if (!file) return;

  uploadError.value = '';
  uploadStats.value = null;
  isProcessing.value = true;

  try {
    // Validar tipo de archivo
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileName = file.name.toLowerCase();
    const isValidFile = validExtensions.some(ext => fileName.endsWith(ext));

    if (!isValidFile) {
      throw new Error(t('CAMPAIGN.BUHOTEXT.PHONE_UPLOADER.INVALID_FILE_TYPE'));
    }

    // Parsear archivo
    const numbers = await parseFile(file);

    if (numbers.length === 0) {
      throw new Error(t('CAMPAIGN.BUHOTEXT.PHONE_UPLOADER.NO_NUMBERS_FOUND'));
    }

    // Validar y procesar números
    await processNumbers(numbers);
  } catch (error) {
    uploadError.value = error.message;
  } finally {
    isProcessing.value = false;
    // Resetear input para permitir subir el mismo archivo nuevamente
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
};

const downloadTemplate = () => {
  // Crear datos de ejemplo
  const templateData = [
    ['Phone Number'],
    ['+12025551234'],
    ['+442071234567'],
    ['+5215512345678'],
  ];

  // Crear workbook
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(templateData);

  // Ajustar ancho de columna
  ws['!cols'] = [{ wch: 20 }];

  XLSX.utils.book_append_sheet(wb, ws, 'Phone Numbers');

  // Descargar archivo
  XLSX.writeFile(wb, 'phone_numbers_template.xlsx');
};

const triggerFileInput = () => {
  fileInput.value?.click();
};
</script>

<template>
  <div class="space-y-3">
    <!-- Botones de acción -->
    <div class="grid grid-cols-2 gap-2">
      <Button
        variant="outline"
        size="sm"
        icon="i-lucide-download"
        class="w-full"
        @click="downloadTemplate"
      >
        {{ $t('CAMPAIGN.BUHOTEXT.PHONE_UPLOADER.DOWNLOAD_TEMPLATE') }}
      </Button>

      <Button
        variant="outline"
        size="sm"
        icon="i-lucide-upload"
        class="w-full"
        :is-loading="isProcessing"
        @click="triggerFileInput"
      >
        {{ $t('CAMPAIGN.BUHOTEXT.PHONE_UPLOADER.UPLOAD_FILE') }}
      </Button>

      <input
        ref="fileInput"
        type="file"
        accept=".xlsx,.xls,.csv"
        class="hidden"
        @change="handleFileUpload"
      />
    </div>

    <!-- Contador de números -->
    <div
      v-if="phoneNumberCount > 0"
      class="flex items-center gap-2 text-sm text-n-slate-11"
    >
      <Icon icon="i-lucide-phone" class="size-4" />
      <span>
        {{
          $t('CAMPAIGN.BUHOTEXT.PHONE_UPLOADER.PHONE_COUNT', {
            count: phoneNumberCount,
          })
        }}
      </span>
    </div>

    <!-- Estadísticas de upload -->
    <div
      v-if="uploadStats"
      class="rounded-lg border p-3 space-y-2"
      :class="{
        'border-n-green-6 bg-n-green-3': uploadStats.invalid === 0,
        'border-n-yellow-6 bg-n-yellow-3': uploadStats.invalid > 0,
      }"
    >
      <div class="flex items-start gap-2 text-sm">
        <Icon
          :icon="
            uploadStats.invalid === 0
              ? 'i-lucide-check-circle'
              : 'i-lucide-alert-triangle'
          "
          class="size-4 flex-shrink-0 mt-0.5"
          :class="{
            'text-n-green-11': uploadStats.invalid === 0,
            'text-n-yellow-11': uploadStats.invalid > 0,
          }"
        />
        <div class="flex-1">
          <p
            class="font-medium"
            :class="{
              'text-n-green-12': uploadStats.invalid === 0,
              'text-n-yellow-12': uploadStats.invalid > 0,
            }"
          >
            {{
              $t('CAMPAIGN.BUHOTEXT.PHONE_UPLOADER.UPLOAD_STATS', uploadStats)
            }}
          </p>

          <div v-if="uploadStats.invalid > 0" class="mt-2 space-y-1">
            <p class="text-xs font-semibold text-n-yellow-12">
              {{ $t('CAMPAIGN.BUHOTEXT.PHONE_UPLOADER.INVALID_NUMBERS') }}:
            </p>
            <ul
              class="text-xs text-n-yellow-11 space-y-0.5 max-h-24 overflow-y-auto"
            >
              <li
                v-for="(num, idx) in uploadStats.invalidNumbers.slice(0, 10)"
                :key="idx"
                class="font-mono"
              >
                {{ num }}
              </li>
              <li v-if="uploadStats.invalidNumbers.length > 10" class="italic">
                {{
                  $t('CAMPAIGN.BUHOTEXT.PHONE_UPLOADER.AND_MORE', {
                    count: uploadStats.invalidNumbers.length - 10,
                  })
                }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div
      v-if="uploadError"
      class="flex items-start gap-2 rounded-lg border border-n-red-6 bg-n-red-3 p-3 text-sm"
    >
      <Icon
        icon="i-lucide-x-circle"
        class="size-4 flex-shrink-0 mt-0.5 text-n-red-11"
      />
      <p class="text-n-red-12">{{ uploadError }}</p>
    </div>

    <!-- Ayuda -->
    <div
      class="text-xs text-n-slate-11 bg-n-blue-3 border border-n-blue-6 rounded-lg p-3"
    >
      <Icon icon="i-lucide-info" class="inline-block mr-1" />
      {{ $t('CAMPAIGN.BUHOTEXT.PHONE_UPLOADER.HELP') }}
    </div>
  </div>
</template>
